import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatNum, apiErr } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../components/ui/tooltip";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { toast } from "sonner";
import {
  CalendarClock, Leaf, Users, ShieldCheck, Lock, Unlock, Check, ArrowRight, ArrowLeft,
  Target, TrendingDown, TrendingUp, Info, Sparkles, Save, Plus, FileSearch, AlertTriangle, Library, ListChecks, Calculator,
} from "lucide-react";
import { EstimatorDialog } from "../components/baseline/EstimatorDialog";

const PILLARS = [
  { key: "Environment", icon: Leaf, color: "#00E65B", blurb: "GHG, energy, fuel, water and waste. The base year fixes your emissions reference for reduction tracking." },
  { key: "Social", icon: Users, color: "#60A5FA", blurb: "Safety, workforce, welfare and community metrics measured against their base year." },
  { key: "Governance", icon: ShieldCheck, color: "#FFB800", blurb: "Ethics, compliance, data and supplier governance baselines." },
];
const YEARS = [2020, 2021, 2022, 2023, 2024, 2025, 2026];
const STEPS = ["Intro", "Environment", "Social", "Governance", "Review & Lock"];

function changePct(baseline, current) {
  if (!baseline || current == null) return null;
  return +(((current - baseline) / baseline) * 100).toFixed(1);
}

export default function BaselineWizard() {
  const [step, setStep] = useState(0);
  const [metrics, setMetrics] = useState(null);
  const [forms, setForms] = useState({});
  const [saving, setSaving] = useState({});
  const [summary, setSummary] = useState(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [drillKey, setDrillKey] = useState(null);
  const [catalogMeta, setCatalogMeta] = useState(null);
  const [autoAdding, setAutoAdding] = useState(false);
  const [estimator, setEstimator] = useState(null);
  const [baselineYear, setBaselineYear] = useState(null);
  const [yearInput, setYearInput] = useState(null);
  const [savingYear, setSavingYear] = useState(false);

  const loadMetrics = useCallback(() => {
    api.get("/baseline/metrics").then((r) => {
      setMetrics(r.data.metrics);
      setForms((prev) => {
        const next = { ...prev };
        r.data.metrics.forEach((m) => {
          if (next[m.metric_key]) return;
          next[m.metric_key] = { rationale: m.baseline?.rationale ?? "" };
        });
        return next;
      });
    }).catch((e) => toast.error(apiErr(e)));
  }, []);

  const loadSummary = useCallback(() => {
    api.get("/baseline").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const loadMeta = useCallback(() => {
    api.get("/baseline/catalog").then((r) => setCatalogMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadMetrics(); loadSummary(); loadMeta(); }, [loadMetrics, loadSummary, loadMeta]);

  const loadYear = useCallback(() => {
    api.get("/baseline/year").then((r) => { setBaselineYear(r.data.baseline_year); if (r.data.baseline_year) setYearInput(r.data.baseline_year); }).catch(() => {});
  }, []);
  useEffect(() => { loadYear(); }, [loadYear]);

  const saveBaselineYear = async (y) => {
    setSavingYear(true);
    try {
      const r = await api.put("/baseline/year", { year: Number(y) });
      setBaselineYear(r.data.baseline_year); setYearInput(r.data.baseline_year);
      toast.success(`Baseline year set to FY${r.data.baseline_year}`);
      loadMetrics(); loadSummary();
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingYear(false); }
  };

  const autoAdd = async () => {
    setAutoAdding(true);
    try {
      const r = await api.post("/baseline/catalog/add-all");
      toast.success(r.data.added ? `Added ${r.data.added} standard-aligned metric(s) for ${r.data.regulatory_tier || "your regime"}` : "All material metrics already added");
      loadMetrics(); loadSummary(); loadMeta();
    } catch (e) { toast.error(apiErr(e)); } finally { setAutoAdding(false); }
  };

  const byPillar = useMemo(() => {
    const g = { Environment: [], Social: [], Governance: [] };
    (metrics || []).forEach((m) => { (g[m.pillar] = g[m.pillar] || []).push(m); });
    return g;
  }, [metrics]);

  const setField = (key, field, val) => setForms((f) => ({ ...f, [key]: { ...f[key], [field]: val } }));

  const addToBaseline = async (m) => {
    if (!baselineYear) { toast.error("Set a baseline year first"); return; }
    const f = forms[m.metric_key] || {};
    setSaving((s) => ({ ...s, [m.metric_key]: true }));
    try {
      await api.post("/baseline", {
        pillar: m.pillar, category: m.category, activity_type: m.activity_type,
        base_year: Number(baselineYear), unit: m.unit, rationale: f.rationale || "",
        method: "Derived from activity data",
      });
      toast.success(`${m.has_baseline ? "Updated" : "Added"} — ${m.activity_type} baseline derived from FY${baselineYear}`);
      loadMetrics(); loadSummary();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving((s) => ({ ...s, [m.metric_key]: false })); }
  };

  const lockAll = async () => {
    const open = (summary?.baselines || []).filter((b) => !b.locked && b.complete);
    const skipped = (summary?.baselines || []).filter((b) => !b.locked && !b.complete).length;
    if (!open.length) { toast.info(skipped ? `${skipped} metric(s) are incomplete and can't be locked.` : "All baselines are already locked."); return; }
    try {
      await Promise.all(open.map((b) => api.post(`/baseline/${b.id}/lock`)));
      toast.success(`Locked ${open.length} baseline(s)${skipped ? ` · ${skipped} skipped (incomplete)` : ""}`);
      loadMetrics(); loadSummary();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleLock = async (b) => {
    try {
      await api.post(`/baseline/${b.id}/${b.locked ? "unlock" : "lock"}`);
      loadMetrics(); loadSummary();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const compMap = useMemo(() => {
    const m = {};
    (summary?.baselines || []).forEach((b) => { m[b.metric_key] = b; });
    return m;
  }, [summary]);

  const savedCount = (summary?.baselines || []).length;

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="baseline-wizard-page">
      {/* Header */}
      <div>
        <div className="label-eyebrow mb-1">Data · Baseline Year</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3">
          <CalendarClock className="h-8 w-8 text-accent" /> Baseline Year Wizard
        </h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-3xl">
          Set the fixed reference year for each metric (GHG Protocol base year). All future progress —
          emissions reductions and ESG / KPI improvements — is measured against this baseline for like-for-like comparison.
        </p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="baseline-stepper">
        {STEPS.map((s, i) => (
          <button key={s} onClick={() => setStep(i)} data-testid={`step-${i}`}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs transition-colors ${i === step ? "bg-accent/15 text-accent" : "text-muted-foreground hover:bg-surface-hover"}`}>
            <span className={`h-5 w-5 rounded-full grid place-items-center text-[10px] font-mono ${i === step ? "bg-accent text-black" : "border border-border"}`}>{i + 1}</span>
            {s}
          </button>
        ))}
      </div>

      {/* INTRO */}
      {step === 0 && (
        <div className="space-y-4" data-testid="step-intro">
          <div className="card-flat p-5 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold"><Info className="h-4 w-4 text-accent" /> Why a baseline year?</div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              In GHG Protocol accounting, the <span className="text-foreground font-medium">base year</span> is the fixed historical
              point your reduction targets are measured from. Lestari extends this to every ESG metric and KPI, so a 30% reduction
              claim is always anchored to a verifiable starting point. You can set a <span className="text-foreground font-medium">different base year per metric</span> —
              useful when data quality for some metrics only became reliable in a later year.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
              {PILLARS.map((p) => (
                <div key={p.key} className="rounded-sm border border-border p-3">
                  <p.icon className="h-4 w-4 mb-1" style={{ color: p.color }} />
                  <div className="text-sm font-semibold">{p.key}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{p.blurb}</div>
                  <div className="text-[11px] mt-1.5 font-mono">{(byPillar[p.key] || []).length} metrics</div>
                </div>
              ))}
            </div>
          </div>

          {/* Baseline reference year — required gate before any baseline data */}
          <div className="card-flat p-5 border-accent/30" data-testid="baseline-year-gate">
            <div className="flex flex-wrap items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-accent/10 border border-accent/20 grid place-items-center shrink-0"><CalendarClock className="h-5 w-5 text-accent" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold text-sm">Baseline reference year</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  A single base year must be set before any baseline data can be created. Every metric's baseline is
                  then <span className="text-foreground font-medium">derived from that year's business-activity data</span> — not entered directly.
                </div>
              </div>
              {baselineYear ? (
                <div className="flex items-center gap-2">
                  <Badge variant="success" className="gap-1" data-testid="baseline-year-current"><Check className="h-3 w-3" /> FY{baselineYear}</Badge>
                  <Select value={String(yearInput || baselineYear)} onValueChange={(v) => saveBaselineYear(Number(v))} disabled={savingYear}>
                    <SelectTrigger className="w-28" data-testid="baseline-year-change"><SelectValue /></SelectTrigger>
                    <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>FY{y}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Select value={yearInput ? String(yearInput) : ""} onValueChange={(v) => setYearInput(Number(v))}>
                    <SelectTrigger className="w-32" data-testid="baseline-year-select"><SelectValue placeholder="Select year" /></SelectTrigger>
                    <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>FY{y}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="primary" size="sm" disabled={!yearInput || savingYear} onClick={() => saveBaselineYear(yearInput)} data-testid="set-baseline-year-btn">
                    {savingYear ? "Setting…" : "Set baseline year"}
                  </Button>
                </div>
              )}
            </div>
            {!baselineYear && <p className="text-[11px] text-[#FFB800] mt-2 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Set a baseline year to unlock baseline creation below.</p>}
          </div>

          {/* Regime auto-add */}
          <div className="card-flat p-5 border-accent/30" data-testid="regime-autoadd-card">
            <div className="flex flex-wrap items-start gap-3">
              <div className="h-10 w-10 rounded-sm bg-accent/10 border border-accent/20 grid place-items-center shrink-0"><Library className="h-5 w-5 text-accent" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold text-sm">Pre-populate your standard-aligned baseline set</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Reporting regime: <span className="text-foreground font-medium">{catalogMeta?.regulatory_tier || "—"}</span>
                  {!!(catalogMeta?.frameworks || []).length && <span className="ml-1">· {catalogMeta.frameworks.join(", ")}</span>}
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(catalogMeta?.frameworks || []).map((f) => <Badge key={f} variant="outline" className="text-[9px]">{f}</Badge>)}
                </div>
              </div>
              <Button variant="primary" size="sm" onClick={autoAdd} disabled={autoAdding || !baselineYear || (catalogMeta && catalogMeta.material_unadded === 0)} data-testid="auto-add-all-btn">
                <Sparkles className="h-3.5 w-3.5" /> {autoAdding ? "Adding…" : catalogMeta && catalogMeta.material_unadded === 0 ? "All material metrics added" : `Auto-add ${catalogMeta?.material_unadded ?? ""} material metrics`}
              </Button>
            </div>
          </div>

          {/* Gap-closing checklist */}
          {savedCount > 0 && <GapChecklist summary={summary} onDrill={setDrillKey} />}

          <div className="card-flat p-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">{savedCount} baseline{savedCount === 1 ? "" : "s"} set · {summary?.summary?.complete_count || 0} complete · {summary?.summary?.locked_count || 0} locked</div>
            <Button variant="primary" onClick={() => setStep(1)} disabled={!baselineYear} data-testid="start-wizard-btn">Start with Environment <ArrowRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {/* PILLAR STEPS */}
      {step >= 1 && step <= 3 && (() => {
        const pillar = PILLARS[step - 1];
        const list = byPillar[pillar.key] || [];
        return (
          <div className="space-y-4" data-testid={`step-${pillar.key.toLowerCase()}`}>
            <div className="flex items-center gap-2">
              <pillar.icon className="h-5 w-5" style={{ color: pillar.color }} />
              <h2 className="font-display text-xl font-bold">{pillar.key} baselines</h2>
              <Badge variant="outline" className="text-[10px]">{list.length} metrics</Badge>
              <div className="flex-1" />
              <Button variant="outline" size="sm" onClick={() => setCatalogOpen(true)} data-testid="add-catalog-metric-btn"><Library className="h-3.5 w-3.5" /> Add standard-aligned metric</Button>
            </div>
            {!baselineYear && (
              <div className="card-flat p-3 border-[#FFB800]/40 bg-[#FFB800]/5 text-[11px] text-[#FFB800] flex items-center gap-2" data-testid="no-baseline-year-warn">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Set a baseline reference year on the Intro step before adding baseline data.
              </div>
            )}
            {list.length === 0 && (
              <div className="card-flat p-8 text-center text-sm text-muted-foreground">
                No {pillar.key.toLowerCase()} metrics found yet. Capture some data first in
                <Link to="/data-management" className="text-accent hover:underline"> Data Management</Link>, then return here.
              </div>
            )}
            <div className="space-y-3">
              {list.map((m) => {
                const f = forms[m.metric_key] || {};
                const locked = m.baseline?.locked;
                const yv = m.year_values?.[baselineYear];
                const cm = compMap[m.metric_key];
                const isEmissions = m.category === "GHG Emissions";
                const derived = isEmissions ? (cm?.baseline_co2e ?? yv?.co2e) : (cm?.baseline_value ?? yv?.value);
                const derivedStr = derived != null
                  ? `${formatNum(derived, { decimals: isEmissions ? 1 : 0 })} ${isEmissions ? "tCO₂e" : (m.unit || "")}`
                  : "—";
                return (
                  <div key={m.metric_key} className="card-flat p-4" data-testid={`metric-${m.metric_key}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <div className="font-medium text-sm">{m.activity_type}</div>
                        <div className="text-[11px] text-muted-foreground">{m.category} · unit: {m.unit || "—"}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <EstimatedBadge method={compMap[m.metric_key]?.method} rationale={compMap[m.metric_key]?.rationale} />
                        {compMap[m.metric_key] && <CompletenessChip b={compMap[m.metric_key]} />}
                        {m.has_baseline && (locked
                          ? <Badge variant="warning" className="gap-1"><Lock className="h-3 w-3" /> Locked</Badge>
                          : <Badge variant="success" className="gap-1"><Check className="h-3 w-3" /> Saved</Badge>)}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr_1fr] gap-3 items-end">
                      <div>
                        <div className="label-eyebrow text-[10px] mb-1">Base year</div>
                        <div className="h-10 flex items-center font-mono text-sm px-1" data-testid={`base-year-${m.metric_key}`}>{baselineYear ? `FY${baselineYear}` : "—"}</div>
                      </div>
                      <div>
                        <div className="label-eyebrow text-[10px] mb-1">Baseline value · derived</div>
                        <div className="h-10 flex items-center font-mono text-sm px-1" data-testid={`baseline-value-${m.metric_key}`}>{derivedStr}</div>
                        <div className="text-[10px] text-muted-foreground">Derived from FY{baselineYear || "—"} activity data — recalculated automatically, not entered.</div>
                      </div>
                      <div>
                        <div className="label-eyebrow text-[10px] mb-1">Evidence / rationale</div>
                        <Input value={f.rationale} disabled={locked} placeholder="e.g. PUB water bill, first full year"
                          onChange={(e) => setField(m.metric_key, "rationale", e.target.value)}
                          data-testid={`rationale-${m.metric_key}`} />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-3">
                      {m.is_env && m.estimable && !locked && <Button variant="outline" size="sm" disabled={!baselineYear} onClick={() => setEstimator({ metric: m, baseYear: baselineYear })} data-testid={`estimate-${m.metric_key}`}><Calculator className="h-3.5 w-3.5" /> Estimate from operational data</Button>}
                      {compMap[m.metric_key] && <Button variant="outline" size="sm" onClick={() => setDrillKey(m.metric_key)} data-testid={`drill-${m.metric_key}`}><FileSearch className="h-3.5 w-3.5" /> Drill down</Button>}
                      {locked
                        ? <Button variant="outline" size="sm" onClick={() => toggleLock(m.baseline)} data-testid={`unlock-${m.metric_key}`}><Unlock className="h-3.5 w-3.5" /> Unlock to edit</Button>
                        : <Button variant="primary" size="sm" disabled={saving[m.metric_key] || !baselineYear} onClick={() => addToBaseline(m)} data-testid={`save-${m.metric_key}`}><Save className="h-3.5 w-3.5" /> {saving[m.metric_key] ? "Saving…" : m.has_baseline ? "Update rationale" : "Add to baseline"}</Button>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between pt-1">
              <Button variant="outline" onClick={() => setStep(step - 1)}><ArrowLeft className="h-4 w-4" /> Back</Button>
              <Button variant="primary" onClick={() => setStep(step + 1)} data-testid="next-step-btn">
                {step === 3 ? "Review & lock" : "Next pillar"} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );
      })()}

      {/* REVIEW */}
      {step === 4 && (
        <div className="space-y-4" data-testid="step-review">
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <SummaryCard icon={Target} label="Metrics with a baseline" value={summary.summary.count} />
              <SummaryCard icon={ListChecks} label="Complete / Incomplete" value={`${summary.summary.complete_count}/${summary.summary.incomplete_count}`} />
              <SummaryCard icon={Lock} label="Locked baselines" value={summary.summary.locked_count} />
              <SummaryCard icon={TrendingDown}
                label={(summary.summary.env_reduction_pct || 0) < 0 ? "Env. change vs baseline" : "Env. reduction vs baseline"}
                value={summary.summary.env_reduction_pct != null
                  ? (summary.summary.env_reduction_pct < 0 ? `+${Math.abs(summary.summary.env_reduction_pct)}%` : `${summary.summary.env_reduction_pct}%`)
                  : "—"}
                accent={(summary.summary.env_reduction_pct || 0) > 0} />
            </div>
          )}
          <div className="text-[11px] text-muted-foreground flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-[#FFB800]" /> Incomplete metrics stay unlocked and are excluded from baseline & reduction calculations until their data is complete and evidenced.</div>
          <div className="card-flat overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="p-3">Metric</th><th className="p-3">Base year</th><th className="p-3 text-right">Baseline</th>
                <th className="p-3 text-right">Current</th><th className="p-3 text-right">Change</th><th className="p-3">Completeness</th><th className="p-3">Status</th><th className="p-3"></th>
              </tr></thead>
              <tbody>
                {(summary?.baselines || []).map((b) => {
                  const env = b.pillar === "Environment";
                  const isEmissions = env && b.baseline_co2e != null;
                  const baseShown = isEmissions ? b.baseline_co2e : b.baseline_value;
                  const curShown = isEmissions ? b.current_co2e : b.current_value;
                  const unitShown = isEmissions ? "tCO₂e" : b.unit;
                  const chg = changePct(baseShown, curShown);
                  return (
                    <tr key={b.id} className="border-b border-border/50" data-testid={`review-row-${b.id}`}>
                      <td className="p-3"><div className="font-medium flex items-center gap-2">{b.activity_type} <EstimatedBadge method={b.method} rationale={b.rationale} /></div>
                        <div className="text-[11px] text-muted-foreground">{b.pillar} · {b.category}</div>
                        {!!(b.standards || []).length && <div className="flex flex-wrap gap-1 mt-1">{b.standards.map((s) => <Badge key={s} variant="outline" className="text-[9px] py-0">{s}</Badge>)}</div>}
                      </td>
                      <td className="p-3 font-mono">FY{b.base_year}</td>
                      <td className="p-3 text-right font-mono">{baseShown != null ? formatNum(baseShown, { decimals: isEmissions ? 1 : 0 }) : "—"}<span className="text-[10px] text-muted-foreground ml-1">{unitShown}</span></td>
                      <td className="p-3 text-right font-mono">{curShown != null ? formatNum(curShown, { decimals: isEmissions ? 1 : 0 }) : "—"}</td>
                      <td className="p-3 text-right font-mono">
                        {chg == null ? "—" : (
                          <span className={`inline-flex items-center gap-0.5 ${chg < 0 ? "text-[#00E65B]" : chg > 0 ? "text-[#FFB800]" : "text-muted-foreground"}`}>
                            {chg < 0 ? <TrendingDown className="h-3 w-3" /> : chg > 0 ? <TrendingUp className="h-3 w-3" /> : null}{Math.abs(chg)}%
                          </span>
                        )}
                      </td>
                      <td className="p-3"><CompletenessChip b={b} /></td>
                      <td className="p-3">{b.locked ? <Badge variant="warning" className="gap-1"><Lock className="h-3 w-3" /> Locked</Badge> : b.complete ? <Badge variant="success">Open</Badge> : <Badge variant="outline" className="text-muted-foreground">Excluded</Badge>}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => setDrillKey(b.metric_key)} data-testid={`drill-row-${b.id}`}><FileSearch className="h-3.5 w-3.5" /></Button>
                        {(b.complete || b.locked)
                          ? <Button variant="ghost" size="sm" onClick={() => toggleLock(b)} data-testid={`toggle-lock-${b.id}`}>{b.locked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}</Button>
                          : <span className="text-[10px] text-muted-foreground">incomplete</span>}
                      </td>
                    </tr>
                  );
                })}
                {!summary?.baselines?.length && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No baselines set yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(3)}><ArrowLeft className="h-4 w-4" /> Back</Button>
            <Button variant="primary" onClick={lockAll} data-testid="lock-all-btn"><Lock className="h-4 w-4" /> Lock all complete baselines</Button>
          </div>
        </div>
      )}

      <CatalogDialog open={catalogOpen} onClose={() => setCatalogOpen(false)} pillar={step >= 1 && step <= 3 ? PILLARS[step - 1].key : null}
        onAdded={() => { loadMetrics(); loadSummary(); }} />
      <DrilldownDialog metricKey={drillKey} onClose={() => setDrillKey(null)} onChanged={() => { loadMetrics(); loadSummary(); }} />
      {estimator && (
        <EstimatorDialog
          metric={estimator.metric} baseYear={estimator.baseYear} open={!!estimator}
          onClose={() => setEstimator(null)}
          onApplied={() => { loadMetrics(); loadSummary(); }}
        />
      )}
    </div>
  );
}

function GapChecklist({ summary, onDrill }) {
  const incomplete = (summary?.baselines || []).filter((b) => !b.complete);
  const total = summary?.baselines?.length || 0;
  const done = (summary?.summary?.complete_count) || 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="card-flat p-5" data-testid="gap-checklist">
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow inline-flex items-center gap-1"><ListChecks className="h-3.5 w-3.5 text-accent" /> Gap-closing checklist</div>
        <span className="font-mono text-sm">{done}/{total} ready <span className="text-muted-foreground">({pct}%)</span></span>
      </div>
      {incomplete.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-[#00E65B]"><Check className="h-4 w-4" /> All baseline metrics are complete and assurance-ready.</div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">These metrics still need data to be included in baseline calculations — drill in to see what's missing:</p>
          {incomplete.map((b) => (
            <div key={b.id} className="flex items-center gap-3 rounded-sm border border-[#FFB800]/30 bg-[#FFB800]/5 p-2.5" data-testid={`gap-item-${b.id}`}>
              <AlertTriangle className="h-4 w-4 text-[#FFB800] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{b.activity_type}</div>
                <div className="text-[11px] text-muted-foreground">{b.pillar} · {b.category} · {(b.standards || []).join(", ")}</div>
              </div>
              <span className="font-mono text-xs text-[#FFB800]">{b.completeness}%</span>
              <Button variant="outline" size="sm" onClick={() => onDrill(b.metric_key)} data-testid={`gap-fix-${b.id}`}><FileSearch className="h-3.5 w-3.5" /> Fix</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EstimatedBadge({ method, rationale }) {
  if (!method || !String(method).startsWith("Estimated")) return null;
  const detail = String(method).replace(/^Estimated\s*[—–-]+\s*/, "");
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span data-testid="estimated-badge" className="inline-flex">
            <Badge variant="warning" className="gap-1 cursor-help text-[10px]">
              <Calculator className="h-3 w-3" /> Estimated
            </Badge>
          </span>
        </TooltipTrigger>
        <TooltipContent data-testid="estimated-badge-tooltip">
          <div className="font-semibold mb-0.5">Estimated baseline — derived, not metered</div>
          {detail && <div className="text-muted-foreground">Method: {detail}</div>}
          {rationale && <div className="mt-1 leading-relaxed">{rationale}</div>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function CompletenessChip({ b }) {
  if (b.complete) return <Badge variant="success" className="gap-1 text-[10px]"><Check className="h-3 w-3" /> {b.completeness}% complete</Badge>;
  return <Badge variant="outline" className="gap-1 text-[10px] text-[#FFB800] border-[#FFB800]/40"><AlertTriangle className="h-3 w-3" /> {b.completeness}% — incomplete</Badge>;
}

function CatalogDialog({ open, onClose, pillar, onAdded }) {
  const [catalog, setCatalog] = useState([]);
  const [adding, setAdding] = useState({});
  const load = useCallback(() => { api.get("/baseline/catalog").then((r) => setCatalog(r.data.catalog)).catch(() => {}); }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  const add = async (c) => {
    setAdding((s) => ({ ...s, [c.key]: true }));
    try { await api.post("/baseline/catalog/add", { key: c.key, base_year: 2023 }); toast.success(`Added ${c.name}`); load(); onAdded(); }
    catch (e) { toast.error(apiErr(e)); } finally { setAdding((s) => ({ ...s, [c.key]: false })); }
  };
  const list = pillar ? catalog.filter((c) => c.pillar === pillar) : catalog;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="catalog-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Library className="h-5 w-5 text-accent" /> Standard-aligned metrics{pillar ? ` · ${pillar}` : ""}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Add metrics defined by recognised frameworks. Each value is auto-aggregated from your underlying data; incomplete metrics stay unlocked until evidenced.</p>
        <div className="space-y-2 mt-2">
          {list.map((c) => (
            <div key={c.key} className="card-flat p-3 flex items-center gap-3" data-testid={`catalog-item-${c.key}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{c.name}</div>
                <div className="text-[11px] text-muted-foreground">{c.pillar} · {c.category} · {c.unit}</div>
                <div className="flex flex-wrap gap-1 mt-1">{c.standards.map((s) => <Badge key={s} variant="outline" className="text-[9px] py-0">{s}</Badge>)}</div>
              </div>
              {c.added
                ? <Badge variant="success" className="gap-1"><Check className="h-3 w-3" /> Added</Badge>
                : <Button size="sm" variant="primary" disabled={adding[c.key]} onClick={() => add(c)} data-testid={`catalog-add-${c.key}`}><Plus className="h-3.5 w-3.5" /> Add</Button>}
            </div>
          ))}
          {!list.length && <div className="text-sm text-muted-foreground py-6 text-center">No catalog metrics for this pillar.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DrilldownDialog({ metricKey, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [assessing, setAssessing] = useState(false);
  const [assessment, setAssessment] = useState("");
  useEffect(() => {
    if (!metricKey) { setD(null); setAssessment(""); return; }
    setD(null); setAssessment("");
    api.get("/baseline/metric/drilldown", { params: { metric_key: metricKey } }).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [metricKey]);
  const assess = async () => {
    setAssessing(true);
    try { const r = await api.post("/baseline/metric/assess", { metric_key: metricKey }); setAssessment(r.data.assessment); onChanged(); }
    catch (e) { toast.error(apiErr(e)); } finally { setAssessing(false); }
  };
  const c = d?.completeness;
  return (
    <Dialog open={!!metricKey} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="drilldown-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><FileSearch className="h-5 w-5 text-accent" /> {d?.activity_type || "Metric"} — data lineage</DialogTitle></DialogHeader>
        {!d ? <div className="py-10 text-center text-muted-foreground text-sm">Loading…</div> : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="outline">{d.pillar} · {d.category}</Badge>
              {(d.standards || []).map((s) => <Badge key={s} variant="outline" className="text-[9px]">{s}</Badge>)}
              <span className="text-muted-foreground">derivation: <span className="font-mono text-foreground">{d.derivation?.type || "activity"}</span></span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <MiniStat label={`Baseline FY${d.base_year}`} value={`${formatNum(d.derivation?.type === "scope_sum" ? d.baseline_agg.co2e : d.baseline_agg.value)} ${d.unit || ""}`} />
              <MiniStat label="Periods" value={`${c.months_present}/${c.expected}`} />
              <MiniStat label="Verified / Evidenced" value={`${c.verified}/${c.evidenced} of ${c.total}`} />
              <MiniStat label="Completeness" value={`${c.score}%`} tone={d.complete ? "text-[#00E65B]" : "text-[#FFB800]"} />
            </div>

            <div className="card-flat p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="label-eyebrow inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI accuracy & completeness assessment</div>
                <Button size="sm" variant="primary" onClick={assess} disabled={assessing} data-testid="assess-btn">{assessing ? "Assessing…" : "Run AI assessment"}</Button>
              </div>
              {assessment ? <p className="text-sm leading-relaxed" data-testid="assessment-text">{assessment}</p>
                : <p className="text-sm text-muted-foreground">{d.complete ? "This metric appears complete and evidence-backed. Run the AI check for an assurance verdict." : "This metric is incomplete — it is unlocked and excluded from baseline calculations. Run the AI check to see exactly what's missing."}</p>}
            </div>

            <div>
              <div className="label-eyebrow mb-2">Contributing records — FY{d.base_year} ({d.base_records.length})</div>
              <div className="card-flat overflow-x-auto max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Activity</TableHead><TableHead className="text-right">Value</TableHead><TableHead className="text-right">tCO₂e</TableHead><TableHead>Status</TableHead><TableHead>Source</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {d.base_records.map((r) => (
                      <TableRow key={`${r.source}-${r.id}`} data-testid="drill-record">
                        <TableCell className="font-mono text-xs">{r.period}</TableCell>
                        <TableCell className="text-xs">{r.activity_type}{r.synthetic ? <Badge variant="warning" className="ml-1 text-[8px]" data-testid="lineage-synthetic">Synthetic</Badge> : r.originally_synthetic ? <Badge variant="outline" className="ml-1 text-[8px] border-[#FFB800]/40 text-[#FFB800]" data-testid="lineage-originally-synthetic">Originally synthetic</Badge> : null}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{formatNum(r.value)} <span className="text-muted-foreground">{r.unit}</span></TableCell>
                        <TableCell className="text-right font-mono text-xs">{r.co2e_tonnes != null ? formatNum(r.co2e_tonnes, { decimals: 2 }) : "—"}</TableCell>
                        <TableCell>{r.status === "verified" ? <Badge variant="success" className="text-[9px]">verified</Badge> : <Badge variant="outline" className="text-[9px]">{r.status || "—"}</Badge>}</TableCell>
                        <TableCell className="text-xs max-w-[160px] truncate">{r.source_doc ? <span className="text-foreground">{r.source_doc}</span> : <span className="text-[#FFB800]">missing</span>}</TableCell>
                      </TableRow>
                    ))}
                    {!d.base_records.length && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No underlying records found for FY{d.base_year}. This metric is incomplete.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ label, value, tone }) {
  return (
    <div className="rounded-sm border border-border p-2.5">
      <div className="label-eyebrow text-[9px] text-muted-foreground">{label}</div>
      <div className={`font-mono text-base font-bold ${tone || ""}`}>{value}</div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="card-flat p-4">
      <div className="flex items-center gap-2 text-muted-foreground mb-2"><Icon className="h-4 w-4" /><span className="label-eyebrow text-[10px]">{label}</span></div>
      <div className={`font-mono text-2xl font-bold ${accent ? "text-[#00E65B]" : ""}`}>{value}</div>
    </div>
  );
}
